import { ParsedRecord } from 'src/components/import/ImportParser';
import { PjmLocationCsvColumns } from './pjmLocationParser';
import { CreatePjmLocationDto } from '../dto/PjmLocationDto';
import { PjmLoadReductionType, PjmMeterEquipmentStatus } from '../dto/enums';
import { calcMaxLoadReductionCapability } from '../calcMaxLoadReductionCapability';
import { makeGeneratorInfo } from './makeGeneratorInfo';
import { makeBatteryInfo } from './makeBatteryInfo';

export function mapCsvImportLocationToCreateLocationDto(record: ParsedRecord<PjmLocationCsvColumns>): CreatePjmLocationDto {
    const dto = {} as CreatePjmLocationDto;

    dto.summaryInfo = {
        name: record['Location*'].parsedValue,
        CSP: {
            name: record['CSP*'].parsedValue,
        },
        EDC: {
            name: record['EDC*'].parsedValue,
            accountNo: record['EDC Account Number*'].parsedValue,
        },
        zone: record['Zone*'].parsedValue,
        allowEDCtoSubmitData: record['Allow EDC to submit data (Yes/No)*'].parsedValue,
        allowLSEtoSubmitData: record['Allow LSE to submit data (Yes/No)*'].parsedValue,
        pricingPoint: record['Pricing Point'].parsedValue,
    };

    dto.siteInfo = {
        address: {
            address1: record['Address Line 1*'].parsedValue,
            address2: record['Address Line 2'].parsedValue,
            city: record['City*'].parsedValue,
            state: record['State*'].parsedValue,
            zipCode: record['Zip Code*'].parsedValue,
        },
        businessSegment: record['Business Segment*'].parsedValue,
        utilityResidentialParticipant: record['Utility Residential Participation (Yes/No)*'].parsedValue,
        participantCount: record['# Of Participants'].parsedValue,
        residentialProgramType: record['Utility Residential Program Type'].parsedValue,
    };

    dto.reductionInfo = [
        {
            reductionType: PjmLoadReductionType.MANUFACTURING,
            reductionValue: record['Manufacturing (kW)'].parsedValue || 0,
        },
        {
            reductionType: PjmLoadReductionType.HVAC,
            reductionValue: record['HVAC (kW)'].parsedValue || 0,
        },
        {
            reductionType: PjmLoadReductionType.GENERATOR,
            reductionValue: record['Generator (kW)'].parsedValue || 0,
        },
        {
            reductionType: PjmLoadReductionType.BATTERIES,
            reductionValue: record['Batteries (kW)'].parsedValue || 0,
        },
        {
            reductionType: PjmLoadReductionType.LIGHTING,
            reductionValue: record['Lighting (kW)'].parsedValue || 0,
        },
        {
            reductionType: PjmLoadReductionType.WATER_HEATERS,
            reductionValue: record['Water Heaters (kW)'].parsedValue || 0,
        },
        {
            reductionType: PjmLoadReductionType.REFRIGERATION,
            reductionValue: record['Refrigeration (kW)'].parsedValue || 0,
        },
        {
            reductionType: PjmLoadReductionType.PLUG_LOAD,
            reductionValue: record['Plug Load (kW)'].parsedValue || 0,
        },
        {
            reductionType: PjmLoadReductionType.COMPUTING,
            reductionValue: record['Computing (kW)'].parsedValue || 0,
        },
    ];

    dto.generatorCount = record['Number of Generators'].parsedValue;

    /** if Generator (kW)* > 0 - we need to set default generator info */
    if (dto.generatorCount && dto.generatorCount > 0 && record['Generator (kW)'].parsedValue > 0) {
        dto.generatorInfo = makeGeneratorInfo({
            current: {
                generatorCount: 0,
                generatorInfo: [],
            },
            update: {
                generatorCount: dto.generatorCount,
                generatorKw: record['Generator (kW)'].parsedValue,
            },
        });
    }

    /** if Batteries (kW)* > 0 - we need to set default batteries info */
    if (record['Batteries (kW)'].parsedValue > 0) {
        dto.batteryInfo = makeBatteryInfo({
            current: {
                batteryInfo: [],
            },
            update: {
                batteryKw: record['Batteries (kW)'].parsedValue,
            },
        });
    }

    dto.profileInfo = {
        energyLossFactor: record['Energy Loss Factor'].parsedValue,
        capacityLossFactor: record['Capacity Loss Factor'].parsedValue,
        maxLoad: record['Max Load (kW)*'].parsedValue,
        peakLoadContribution: record['Peak Load Contribution (kW)'].parsedValue,
        winterPeakLoad: record['Winter Peak Load (kW)'].parsedValue,
        loadReduction: calcMaxLoadReductionCapability(dto.reductionInfo),
    };

    dto.meterInfo = [
        {
            meterType: record['Meter Type (EDC_METER)*'].parsedValue,
            meterEquipment: undefined,
            meterStatus: PjmMeterEquipmentStatus.PENDING,
            reviewDate: undefined,
            interval: undefined,
            installationDate: undefined,
            installationQaPlan: undefined,
            certification: undefined,
        },
    ];

    if (record['Registration Name*'].parsedValue) {
        dto.registration = {
            summaryInfo: {
                name: record['Registration Name*'].parsedValue,
                program: record['Program*'].parsedValue,
                startDate: record['Effective Date (MM/DD/YYYY)*'].parsedValue,
                endDate: record['Termination Date (MM/DD/YYYY)*'].parsedValue,
                /** bellow values are copied from location */
                CSP: {
                    name: record['CSP*'].parsedValue,
                },
                EDC: {
                    name: record['EDC*'].parsedValue,
                    accountNo: record['EDC Account Number*'].parsedValue,
                },
                zone: record['Zone*'].parsedValue,
                pricingPoint: record['Pricing Point'].parsedValue,

                residential: record['Residential Registration (Yes/No)'].parsedValue,
                residentialProgramType: record['Residential Program Type'].parsedValue,
                loadDataSample: record['Load Data Sample (Yes/No)'].parsedValue,
                sampleNumberParticipants: record['# Sample Participants'].parsedValue,
            },
            locationSummaryInfo: {
                // copy from location
                energyLossFactor: record['Energy Loss Factor'].parsedValue,
                capacityLossFactor: record['Capacity Loss Factor'].parsedValue,
                peakLoadContribution: record['Peak Load Contribution (kW)'].parsedValue,
                winterPeakLoad: record['Winter Peak Load (kW)'].parsedValue,
                maximumLoadReductionCapability: calcMaxLoadReductionCapability(dto.reductionInfo),
            },
            capacityInfo: {
                capacityType: undefined,
                zoneArea: undefined,
                RPMResourceID: record['RPM DR Resource ID'].parsedValue,
                measurementMethod: record['Measurement Method'].parsedValue,
                capacityLossFactor: record['Capacity Loss Factor'].parsedValue, // !copy from location - it seems DRHUB does not return this
                peakLoadContribution: record['Peak Load Contribution (kW)'].parsedValue, // !copy from location - it seems DRHUB does not return this
                winterPeakLoad: undefined,
                summerManagedLoad: record['Summer Managed Load (kW)'].parsedValue,
                winterManagedLoad: record['Winter Managed Load (kW)'].parsedValue,
                summerWeatherAdjustmentFactor: undefined,
                winterWeatherAdjustmentFactor: undefined,
                participantCount: undefined,
                summerNominatedICAP: undefined,
                winterNominatedICAP: undefined,
                annualNominatedICAP: undefined,
                product: record['Product'].parsedValue,
                resourceType: record['Resource Type'].parsedValue,
                leadTime: record['Lead Time*'].parsedValue,
                leadExceptionCertify: record['Lead Exception Documentation (Yes/No)'].parsedValue,
                leadExceptionReason: record['Lead Exception Reason'].parsedValue,
                leadExceptionDesc: record['Lead Exception Description'].parsedValue,
                summerOnly: record['Summer Only (Yes/No)*'].parsedValue,
            },
            energyInfo: {
                loadReduction: undefined,
                energyLossFactor: undefined,
                strikePrice: record['Strike Price (US$)'].parsedValue,
                shutdownCosts: record['Shutdown Cost (US$)'].parsedValue,
                contractType: undefined,
                priceNode: undefined,
                retailRate: undefined,
                rateName: undefined,
                rateDescription: undefined,
                dayAhead: undefined,
                realTime: undefined,
                scheduledReserve: undefined,
                secondaryReserve: undefined,
                synchronizedReserve: undefined,
                flexible: undefined,
                inflexible: undefined,
                regulation: undefined,
                batchLoad: undefined,
                secrArea: undefined,
                secrSCADATestedAndVerified: undefined,
                srArea: undefined,
                srSCADATestedAndVerified: undefined,
                regulationSCADAName: undefined,
                regulationSCADATestedAndVerified: undefined,
                regulationTestCompleted: undefined,
                submeter: undefined,
                regulationCertify: undefined,
            },
            otherInfo: {
                allowEDCtoModify: record['Allow Modification EDC (Yes/No)*'].parsedValue,
                allowLSEtoModify: record['Allow Modification LSE (Yes/No)*'].parsedValue,
            },
        };
    }

    return dto;
}
